import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type {
  PracticeLabResponse,
  PracticeLabSessionResponse,
} from "@/types/practice-lab";

// List the labs the user can access. Pass `standaloneOnly` to get only
// independent (non-chapter) labs — the dashboard's "Practice Labs" section.
export function usePracticeLabs(options?: {
  standaloneOnly?: boolean;
  enabled?: boolean;
}) {
  const standaloneOnly = options?.standaloneOnly ?? false;
  return useQuery<PracticeLabResponse[]>({
    queryKey: ["practice-labs", { standaloneOnly }],
    queryFn: () =>
      apiFetch<PracticeLabResponse[]>(
        `/practice-labs${standaloneOnly ? "?standalone=true" : ""}`
      ),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

// Get a single practice lab with its ordered steps (static, served content).
export function usePracticeLab(id: number) {
  return useQuery<PracticeLabResponse>({
    queryKey: ["practice-lab", id],
    queryFn: () => apiFetch<PracticeLabResponse>(`/practice-labs/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // labs rarely change between publishes
  });
}

// Get the user's active session for a lab, or null when none exists (the
// server returns 204 and apiFetch resolves an empty body to null).
//
// While there's no active session we poll: the session is created by the CLI's
// `lab start`, and the design's first-run page "connects automatically the
// moment the lab starts". Once a session exists, polling stops — live updates
// come over SSE from then on.
export function useActiveSession(
  labId: number,
  options?: { enabled?: boolean }
) {
  return useQuery<PracticeLabSessionResponse | null>({
    queryKey: ["practice-lab-session", labId],
    queryFn: () =>
      apiFetch<PracticeLabSessionResponse | null>(
        `/practice-labs/${labId}/sessions/active`
      ),
    enabled: (options?.enabled ?? true) && !!labId,
    refetchInterval: (query) => (query.state.data ? false : 4000),
  });
}
